import { UserApi } from "@/api/user.api";
import { StytchClient } from "@/stores/stytch";
import { Button, Loader, PasswordInput } from "@mantine/core";
import Cookies from "js-cookie";
import { FormEvent, useEffect, useState } from "react";

// 2026-07-30: the reset_password branch previously used the pre-built
// StytchPasswordReset widget, which (like the old login widget) never
// submitted a native form with autocomplete="new-password" — so Keychain
// never offered to save the new password. Replaced with a headless form
// using StytchClient.passwords.resetByEmail. Magic-link and OAuth token
// handling unchanged.

export const AuthCallback = () => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleLoginSuccess = async () => {
    // We can get the User and Session objects using getSync().
    const user = StytchClient.user.getSync();
    const session = StytchClient.session.getSync();

    console.log(
      "[auth] handleLoginSuccess — session expires_at:",
      session?.expires_at ?? "null",
      "| created_at:",
      session?.started_at ?? "null",
      "| user_id:",
      user?.user_id ?? "null"
    );

    if (user && session) {
      Cookies.set("stytch_user_id", session.user_id, { expires: 1 });
      Cookies.set("stytch_user_email", user.emails[0].email, { expires: 1 });
      Cookies.remove("fileUrlCompressed");
      Cookies.remove("fileUrlThumbnail");
      Cookies.remove("fileName");
      await UserApi.updateSession({ session, user }).catch(() => null);

      const returnUrl = localStorage.getItem("login_return_url") || "/";
      localStorage.removeItem("login_return_url");
      window.location.href = returnUrl;
    }
  };

  // Look for the Stytch token in query params.
  const queryParams = new URLSearchParams(window.location.search);
  const token = queryParams.get("token");
  const tokenType = queryParams.get("stytch_token_type");

  // If a token is found, authenticate it with the appropriate method.
  useEffect(() => {
    if (token && tokenType) {
      if (tokenType === "magic_links" || tokenType === "login") {
        StytchClient.magicLinks
          .authenticate(token, { session_duration_minutes: 1440 })
          .then(handleLoginSuccess)
          .catch(err => {
            console.error(err);
            console.log(
              "Email Magic Link authentication failed. See console for details."
            );
          });
      } else if (tokenType === "oauth") {
        console.log("oauth");
        StytchClient.oauth
          .authenticate(token, { session_duration_minutes: 1440 })
          .then(handleLoginSuccess)
          .catch(err => {
            console.error(err);
            console.log(
              "OAuth authentication failed. See console for details."
            );
          });
      }
    } else {
      // If query params are not found, announce that something went wrong.
      console.log("Something went wrong. No token found to authenticate.");
    }
  }, []);

  const handlePasswordReset = async (e: FormEvent) => {
    e.preventDefault();
    if (!password || !token) return;
    setBusy(true);
    setError("");
    try {
      const strength = await StytchClient.passwords.strengthCheck({
        password,
      });
      if (!strength.valid_password) {
        setError(
          strength.feedback?.warning ||
            strength.feedback?.suggestions?.[0] ||
            "Please choose a longer, less common password."
        );
        setBusy(false);
        return;
      }
      await StytchClient.passwords.resetByEmail({
        token,
        password,
        session_duration_minutes: 1440,
      });
      await handleLoginSuccess();
    } catch (err: any) {
      console.error(err);
      setError(
        err?.error_message ||
          "Couldn't set that password. The link may have expired — request a new one from the sign-in screen."
      );
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4">
      {tokenType === "reset_password" ? (
        <form
          onSubmit={handlePasswordReset}
          method="post"
          action="#"
          style={{
            width: "min(420px, 90%)",
            fontFamily: "Afacad",
          }}
        >
          <p
            style={{
              fontSize: 22,
              fontWeight: 600,
              textAlign: "center",
              margin: "0 0 16px",
            }}
          >
            Set your new password
          </p>
          <PasswordInput
            name="new-password"
            label="New password"
            autoComplete="new-password"
            value={password}
            onChange={e => setPassword(e.currentTarget.value)}
            styles={{
              input: { fontFamily: "Afacad", fontSize: 16, height: 46 },
              label: { fontFamily: "Afacad", fontSize: 15, marginBottom: 4 },
            }}
            autoFocus
            required
          />
          {error && (
            <p style={{ color: "#c92a2a", fontSize: 14, margin: "8px 0 0" }}>
              {error}
            </p>
          )}
          <Button
            type="submit"
            fullWidth
            size="lg"
            mt={14}
            loading={busy}
            style={{
              backgroundColor: "#4AA0A0",
              borderColor: "#4AA0A0",
              color: "white",
              fontFamily: "Afacad",
              height: "48px",
              fontSize: "18px",
              fontWeight: 500,
              borderRadius: "4px",
            }}
          >
            Save password and sign in
          </Button>
        </form>
      ) : (
        <>
          <Loader size="lg" color="#4AA0A0" />
          <p className="text-lg text-[#636363]">Authenticating...</p>
        </>
      )}
    </div>
  );
};
